import { Fragment, useMemo, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import { parseMarkdown, type Inline } from "./markdown";

const renderInline = (nodes: Inline[]): ReactNode =>
  nodes.map((node, index) => {
    switch (node.type) {
      case "text":
        return <Fragment key={index}>{node.text}</Fragment>;
      case "strong":
        return (
          <strong key={index} className="font-semibold text-foreground">
            {renderInline(node.children)}
          </strong>
        );
      case "em":
        return <em key={index}>{renderInline(node.children)}</em>;
      case "code":
        return (
          <code
            key={index}
            className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]"
          >
            {node.text}
          </code>
        );
      case "link":
        return (
          <a
            key={index}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            {renderInline(node.children)}
          </a>
        );
    }
  });

interface MiniMarkdownProps {
  source: string | null | undefined;
  className?: string;
}

/** Renders the markdown subset of `markdown.ts` as React elements (no HTML). */
export const MiniMarkdown = ({ source, className }: MiniMarkdownProps) => {
  const blocks = useMemo(() => parseMarkdown(source), [source]);
  if (!blocks.length) return null;

  return (
    <div
      className={cn(
        "mi-markdown min-w-0 space-y-2 text-[15px] leading-relaxed wrap-anywhere",
        className,
      )}
    >
      {blocks.map((block, index) => {
        if (block.type === "heading")
          return (
            <p
              key={index}
              className="pt-1 text-[13px] font-semibold tracking-wide text-foreground uppercase"
            >
              {renderInline(block.children)}
            </p>
          );
        if (block.type === "list") {
          const List = block.ordered ? "ol" : "ul";
          return (
            <List
              key={index}
              className={cn(
                "space-y-1 pl-5",
                block.ordered ? "list-decimal" : "list-disc",
              )}
            >
              {block.items.map((item, itemIndex) => (
                <li
                  key={itemIndex}
                  className="pl-0.5 marker:text-muted-foreground"
                >
                  {renderInline(item)}
                </li>
              ))}
            </List>
          );
        }
        return <p key={index}>{renderInline(block.children)}</p>;
      })}
    </div>
  );
};
